import React, { useState } from 'react';
import { DownOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';

// 16px text on phones: iOS Safari zooms into any input smaller than that.
const inputClass =
  'w-full px-3 py-2.5 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent';

const STATUS_STYLES = {
  found: 'bg-green-50 text-green-700',
  guessed: 'bg-amber-50 text-amber-700',
  missing: 'bg-gray-100 text-gray-500',
};
const STATUS_LABELS = {
  found: 'businessCardScanner.found',
  guessed: 'businessCardScanner.guessed',
  missing: 'businessCardScanner.notFound',
};

const FieldLabel = ({ field, htmlFor, status }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-2 mb-1">
      <label htmlFor={htmlFor} className="text-xs font-medium text-gray-700">
        {t(`businessCardScanner.fields.${field}`)}
      </label>
      {status && (
        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[status]}`}>
          {t(STATUS_LABELS[status])}
        </span>
      )}
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h6 className="text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</h6>
    {children}
  </div>
);

// Contact fields for a business card, shared by the scan review and the edit modal.
// `detected` (scan review only) maps a field to 'found' | 'guessed' | 'missing'.
const BusinessCardForm = ({ value, onChange, availableTags = [], detected }) => {
  const { t } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(() => Boolean(value.address || value.notes));

  const set = (field) => (e) => onChange({ ...value, [field]: e.target.value });

  const field = (name, { type = 'text', ltr = false } = {}) => (
    <div key={name}>
      <FieldLabel field={name} htmlFor={`bc-${name}`} status={detected?.[name]} />
      <input
        id={`bc-${name}`}
        type={type}
        dir={ltr ? 'ltr' : undefined}
        // Phone keyboards would otherwise turn "www.x.com" into "Www.x.com"
        autoCapitalize={ltr ? 'none' : undefined}
        autoCorrect={ltr ? 'off' : undefined}
        spellCheck={ltr ? false : undefined}
        value={value[name] || ''}
        onChange={set(name)}
        className={inputClass}
        placeholder={t(`businessCardScanner.placeholders.${name}`)}
      />
    </div>
  );

  return (
    <div className="space-y-5">
      <Section title={t('businessCardScanner.sections.contact')}>
        {field('name')}
        {field('position')}
        {field('company')}
      </Section>

      <Section title={t('businessCardScanner.sections.reach')}>
        {field('phone', { type: 'tel', ltr: true })}
        {field('email', { type: 'email', ltr: true })}
        {field('website', { ltr: true })}
      </Section>

      <div className="border-t border-gray-100 pt-3">
        <button
          type="button"
          onClick={() => setMoreOpen((open) => !open)}
          className="w-full flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-gray-400"
          aria-expanded={moreOpen}
        >
          {t('businessCardScanner.sections.more')}
          <DownOutlined className={`transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
        </button>

        {moreOpen && (
          <div className="space-y-3 mt-3">
            <div>
              <FieldLabel field="address" htmlFor="bc-address" status={detected?.address} />
              <textarea
                id="bc-address"
                value={value.address || ''}
                onChange={set('address')}
                className={`${inputClass} resize-none`}
                placeholder={t('businessCardScanner.placeholders.address')}
                rows="2"
              />
            </div>
            <div>
              <FieldLabel field="tag" />
              <Select
                mode="tags"
                size="large" // 16px, so iOS doesn't zoom in on its search input
                style={{ width: '100%' }}
                placeholder={t('businessCardScanner.placeholders.tag')}
                value={value.tag ? [value.tag] : []}
                // Single tag: keep only the most recent choice.
                onChange={(tags) => onChange({ ...value, tag: tags[tags.length - 1] })}
                options={availableTags.map((tag) => ({ value: tag, label: tag }))}
              />
            </div>
            <div>
              <FieldLabel field="notes" htmlFor="bc-notes" />
              <textarea
                id="bc-notes"
                value={value.notes || ''}
                onChange={set('notes')}
                className={`${inputClass} resize-none`}
                placeholder={t('businessCardScanner.placeholders.notes')}
                rows="3"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessCardForm;
